import React, {useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {Skeleton} from 'src/components/skeleton';
import {MessageContext} from 'src/contexts/message-context';
import {getCreditAlertSettings, saveCreditAlertSettings} from 'src/openapi/admin/apis/AlertControllerApi';
import {getContractsBasic} from 'src/openapi/admin/apis/ContractControllerApi';
import {getUsersByAccountId} from 'src/openapi/admin/apis/UserControllerApi';
import {useAppDispatch} from 'src/store/hooks';

import Loading from '@pb/kit-component-loading';
import {ConcurrentStrategies} from '@pb/middleware-api';

import {setupDataSelector} from '../setup/services/selectors';

import {clearError as clearErrorAction} from './services/actions';
import {AlertsView} from './views/alerts.view';

import type {TabProps} from '../../typedef';
import type {Contract} from 'src/openapi/admin/models/Contract';
import type {CreditAlertSettings} from 'src/openapi/admin/models/CreditAlertSettings';
import type {Error} from 'src/openapi/admin/models/Error';
import type {UserList} from 'src/openapi/admin/models/UserList';
import type {AppState} from 'src/store/typedef';


type OwnProps = TabProps;

type StateProps = {
    alertsData: CreditAlertSettings | null,
    alertUsers: UserList | null,
    activeContracts: Array<Contract> | null,
    error: Error | null,
    isCrmIntegration?: boolean,
};

type Props = OwnProps & StateProps;

const AlertsController = (props: Props) => {
    const [data, setData] = useState<CreditAlertSettings | null>(null);
    const [users, setUsers] = useState<UserList | null>(null);
    const [alertsBusy, setAlertsBusy] = useState<boolean>(false);

    const {accountId, error, activeContracts, isCrmIntegration, alertsData, alertUsers} = props;

    const dispatch = useDispatch();
    const appDispatch = useAppDispatch();
    const {setErrorMessage} = useContext(MessageContext);

    useEffect(() => {
        appDispatch(getCreditAlertSettings({accountId}, {concurrentStrategy: ConcurrentStrategies.LAST}));
        appDispatch(getUsersByAccountId({accountId}, {concurrentStrategy: ConcurrentStrategies.LAST}));
        appDispatch(getContractsBasic({accountId}));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (alertsData) {
            setData(alertsData);
        }
        if (alertUsers) {
            setUsers(alertUsers);
        }
    }, [alertUsers, alertsData]);

    useEffect(() => {
        if (error) {
            setAlertsBusy(false);
            setErrorMessage(error.message || 'Service is temporarily unavailable');
        }
    }, [error, setErrorMessage]);

    const onUpdateAlerts = async (creditAlertSettings: CreditAlertSettings) => {
        setAlertsBusy(true);
        const response = await appDispatch(saveCreditAlertSettings({accountId, creditAlertSettings}));

        if (response.payload.success) {
            await appDispatch(getCreditAlertSettings({accountId}));
        }
        setAlertsBusy(false);
    };

    const onCloseError = () => {
        dispatch(clearErrorAction());
    };

    return data && users && activeContracts ? (
        <AlertsView
            {...{
                data,
                users,
                activeContracts,
                error,
                alertsBusy,
                isCrmIntegration,
                onUpdateAlerts,
                onCloseError,
            }}
        />
    ) : (
        <Loading/>
    );
};

const AlertsControllerWrapper = ({accountId}: OwnProps) => {
    const alertsData = useSelector((state: AppState) => state.apiv2.alertsData);

    const data = (alertsData.data as Record<number, CreditAlertSettings>)[accountId] ?? null;
    const alertUsers = (alertsData.users as Record<number, UserList>)[accountId] ?? null;
    const activeContracts = (alertsData.activeContracts as Record<number, Array<Contract>>)[accountId] ?? null;

    const setupSetting = useSelector(setupDataSelector);

    return (
        <Skeleton shown={!setupSetting} view={<Loading fast={true}/>}>
            <AlertsController {...{
                accountId,
                alertsData: data,
                alertUsers,
                activeContracts,
                error: alertsData.error,
                isCrmIntegration: setupSetting?.crmIntegration,
            }}
            />
        </Skeleton>
    );
};

export default AlertsControllerWrapper;
